import {Fragment, useContext, useEffect} from "react";
import {backend} from "../../model/Backend";
import {BasicButton} from "../../common/Button";
import {UserActions, UserDispatchContext} from "../../contexts/UserContext";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBookmark, faBolt, faUser, faXmark} from '@fortawesome/free-solid-svg-icons'
import {UserActivityType} from "../../model/User";
import {DateUtils} from "../../common/Utils";
import {SetCanvasStateContext} from "../../contexts/CanvasContext";
import {RouteFocusState} from "../../scene/canvas/CanvasState";
import ActivityCard from "./ActivityCard";
import BookmarkCard from "./BookmarkCard";
import {ScreenDimensions, useScreenDimensions} from "../../common/CustomHooks";
import {UiActions, UiDispatchContext} from "../../contexts/UiContext";

export default function AccountInfo({user}) {
    const userDispatch = useContext(UserDispatchContext);
    const setCanvasState = useContext(SetCanvasStateContext);
    const uiStateDispatch = useContext(UiDispatchContext);
    const [screenWidth, _] = useScreenDimensions();

    function signOut() {
        backend.logout();
        userDispatch({
            type: UserActions.Logout
        });
    }

    function closeSidePanelIfMedium() {
        if (screenWidth <= ScreenDimensions.MEDIUM_WIDTH) {
            uiStateDispatch({type: UiActions.CloseSidePanel});
        }
    }

    function handleBookmarkRemove(bookmarkActivity) {
        console.assert(bookmarkActivity.type === UserActivityType.Bookmark);
        userDispatch({
            type: UserActions.RemoveActivity,
            activity: {
                type: UserActivityType.Bookmark,
                routeId: bookmarkActivity.route.id,
            },
        });
    }

    function handleBookmarkClick(bookmark) {
        if (bookmark.wallRoute !== null) {
            setCanvasState(new RouteFocusState(bookmark.wallRoute));
            closeSidePanelIfMedium();
        } else {
            // TODO: show a pop up explaining that the route no longer exists
        }
    }

    function handleActivityClick(activity) {
        if (activity.wallRoute !== null) {
            setCanvasState(new RouteFocusState(activity.wallRoute));
            closeSidePanelIfMedium();
        } else {
            // TODO: show a pop up explaining that the route no longer exists
        }
    }

    let activitiesList = [];
    let lastActivityTimestamp = null;
    for (let i = user.value.activities.length - 1; i >= 0; i--) {
        const activity = user.value.activities[i];
        if (![UserActivityType.Attempt, UserActivityType.Climb, UserActivityType.Flash].includes(activity.type))
            continue;

        const key = `${i}${activity.route.routeId}${activity.type}`;
        if (lastActivityTimestamp === null || !DateUtils.isSameDay(activity.timestamp, lastActivityTimestamp)) {
            const dateString = activity.timestamp.toLocaleDateString("en-US", {
                weekday: "long", year: "numeric", month: "long", day: "numeric"
            });
            activitiesList.push(
                <Fragment key={"date" + key}>
                    <div className="mt-3"></div>
                    <span className="font-semibold">{dateString}</span>
                </Fragment>
            );
        }
        activitiesList.push(
            <Fragment key={"activity" + key}>
                <ActivityCard activity={activity} onActivityClick={handleActivityClick} listIdx={i}/>
            </Fragment>
        );
        lastActivityTimestamp = activity.timestamp;
    }

    let bookmarksList = [];
    for (let i = user.value.activities.length - 1; i >= 0; i--) {
        const bookmark = user.value.activities[i];
        if (bookmark.type !== UserActivityType.Bookmark)
            continue;
        const key = `${i}${bookmark.route.routeId}`;
        bookmarksList.push(
            <Fragment key={"bookmark" + key}>
                <BookmarkCard bookmark={bookmark} onBookmarkClick={handleBookmarkClick}
                              onBookmarkRemove={handleBookmarkRemove}/>
            </Fragment>
        );
    }

    return (
        <div id="account-info">
            <h2 className="text-2xl"><FontAwesomeIcon icon={faUser} className="mr-3" size="sm"/>{user.value.email}</h2>
            <div className="mb-4"></div>

            <div className="flex justify-left space-x-4">
                <BasicButton onClick={signOut}>
                    Sign Out
                </BasicButton>
                <BasicButton>
                    Settings
                </BasicButton>
            </div>

            <div className="mb-7"></div>
            <h3>
                <FontAwesomeIcon icon={faBookmark} className="mr-3 mb-0.5" size="xl"/>
                <span className="text-3xl">Bookmarks</span>
            </h3>
            <div className="mb-3"></div>
            {bookmarksList.length === 0 &&
                <p>You have no bookmarked routes on this wall yet!</p>
            }
            {bookmarksList}

            <div className="mb-5"></div>
            <h3>
                <FontAwesomeIcon icon={faBolt} className="mr-2" size="2x"/>
                <span className="text-3xl">Activity</span>
            </h3>
            <div className="mb-2"></div>
            {activitiesList.length === 0 &&
                <p>You have no activity on this wall yet!</p>
            }
            {activitiesList}
        </div>
    );
}